import React, { useState, useEffect } from "react";
import { X, Check, Edit3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface EditVariableModalProps {
  isOpen: boolean;
  variableName: string;
  initialValue?: string;
  onClose: () => void;
  onSave: (newValue: string) => void;
}

export const EditVariableModal: React.FC<EditVariableModalProps> = ({
  isOpen,
  variableName,
  initialValue = "",
  onClose,
  onSave,
}) => {
  const [val, setVal] = useState(initialValue);
  const cleaned = variableName.replace(/^\[|\]$/g, "").trim();

  // Detección dinámica: ¿requiere Textarea o Input?
  const isMultiline =
    cleaned.length > 40 ||
    initialValue.length > 50 ||
    /[\n\r]|describir|hecho|motivo|razon|pretension|servicio|observacion|detalle|clausula|direccion|explic/i.test(cleaned);

  useEffect(() => {
    if (isOpen) {
      setVal(initialValue || "");
    }
  }, [isOpen, initialValue]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (val.trim()) {
      onSave(val.trim());
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-amber-50/80 via-indigo-50/40 to-white">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-600">
              <Edit3 className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">Editar Variable</h3>
              <p className="text-[11px] font-mono text-amber-700 truncate max-w-[280px]">[{cleaned}]</p>
            </div>
          </div>
          <button 
            type="button"
            onClick={onClose} 
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-700">
              {isMultiline ? "Detalle o contenido descriptivo (Multilínea):" : "Valor del campo:"}
            </label>
            {isMultiline ? (
              <Textarea
                rows={4}
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder={`Ingrese el texto detallado para [${cleaned}]...`}
                className="text-xs min-h-[110px] resize-y bg-slate-50 focus:bg-white"
                autoFocus
              />
            ) : (
              <Input
                type="text"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder={`Ingrese valor para [${cleaned}]...`}
                className="text-xs h-10 bg-slate-50 focus:bg-white"
                autoFocus
              />
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="ghost" size="sm" onClick={onClose} className="text-xs font-semibold text-slate-600">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              size="sm" 
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 text-xs font-bold shadow-sm"
              disabled={!val.trim()}
            >
              <Check className="w-3.5 h-3.5" /> Guardar y Aplicar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
